/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ThunkAction } from 'redux-thunk';
import {
	AppConfigActionTypes,
	GET_APP_CONFIGS_FAILURE,
	GET_APP_CONFIGS_REQUEST,
	GET_APP_CONFIGS_SUCCESS,
	GetAppConfigsFailureAction,
	GetAppConfigsRequestAction,
	GetAppConfigsSuccessAction
} from './AppConfigActionTypes';
import { BaseError } from '../../../shared/errors/Errors';
import { wrapIntoErrorObject } from '../../../shared/errors/ErrorWrapper';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../store/store';
import { ClientTenantAndAppVariantEntry } from '@DigitaleDoerfer/digitale-doerfer-api';
import { sharedAdminuiAppApi } from '../../../ServiceFactory';

export type ThunkResult<R> = ThunkAction<R, PlatformAdminUIState, undefined, AppConfigActionTypes>;

export const getAppConfigsRequest = (): GetAppConfigsRequestAction => {
	return {
		type: GET_APP_CONFIGS_REQUEST
	};
};

export const getAppConfigsSuccess = (appConfigs: ClientTenantAndAppVariantEntry[]): GetAppConfigsSuccessAction => {
	return {
		type: GET_APP_CONFIGS_SUCCESS,
		appConfigs
	};
};

export const getAppConfigsFailure = (error: BaseError): GetAppConfigsFailureAction => {
	return {
		type: GET_APP_CONFIGS_FAILURE,
		error
	};
};

export const getAppConfigs = (): ThunkResult<Promise<void>> => {
	return async (
		dispatch: PlatformAdminUIThunkType<AppConfigActionTypes>,
		getState: () => PlatformAdminUIState
	): Promise<void> => {
		try {
			dispatch(getAppConfigsRequest());

			// Let's get the app configurations
			const response = await sharedAdminuiAppApi().appVariantByTenant({});

			// Prepate data to dispatch getAppConfigsSuccess
			const appConfigs = response ? response : [];
			dispatch(getAppConfigsSuccess(appConfigs));
		} catch (error) {
			dispatch(getAppConfigsFailure(wrapIntoErrorObject(error)));
		}
	};
};
